import React, {useEffect, useState} from 'react';
import {Link, useParams} from 'react-router-dom';
import {adminLearnerStateApi, getErrorMessage} from '../services/api';
import {CoverageVisual, faDate, faNumber, faPercent, Freshness, MasteryVisual} from './StateVisuals';
import './AdminLearnerState.css';

const PAGE_SIZE = 25;
const reasonNames = {
    insufficient_evidence: 'شواهد کافی برای مقایسهٔ ۳۰ روزه نیست',
    unknown_resource_version: 'نسخهٔ تاریخی منبع نامشخص است',
    resource_version_changed: 'نسخهٔ منبع تغییر کرده است',
    algorithm_version_changed: 'نسخهٔ الگوریتم تغییر کرده است',
};
const learnerPath = (row, courseId) => `/learner-state/${encodeURIComponent(row.learner_id)}?course=${encodeURIComponent(courseId)}`;
const detailPath = (row, courseId) => row.graph_id != null
    ? `/learner-state/${encodeURIComponent(row.learner_id)}/graphs/${encodeURIComponent(row.graph_id)}?course=${encodeURIComponent(courseId)}`
    : `/learner-state/${encodeURIComponent(row.learner_id)}/resources/${encodeURIComponent(row.resource_id)}?course=${encodeURIComponent(courseId)}`;

function Change({row}) {
    const delta = Number(row.mastery_delta_30d);
    if (row.mastery_delta_30d == null || !Number.isFinite(delta)) {
        return <span className="als-delta-reason" title={row.delta_reason || ''}>{reasonNames[row.delta_reason] || 'تغییر ۳۰ روزه قابل محاسبه نیست'}</span>;
    }
    return <span className={`als-delta ${delta > 0 ? 'up' : delta < 0 ? 'down' : 'same'}`}
        title={`از ${faDate(row.baseline_at)} تا ${faDate(row.current_at)}`}>
        {delta > 0 ? '↑ ' : delta < 0 ? '↓ ' : ''}{Math.round(Math.abs(delta) * 100).toLocaleString('fa-IR')} نقطهٔ درصدی
    </span>;
}

export default function CourseLearnerMonitor() {
    const {courseId} = useParams();
    const [query, setQuery] = useState('');
    const [search, setSearch] = useState('');
    const [offset, setOffset] = useState(0);
    const [retry, setRetry] = useState(0);
    const [state, setState] = useState({data: null, loading: true, error: null, fetchedAt: null});
    useEffect(() => {
        const timer = setTimeout(() => { setOffset(0); setSearch(query.trim()); }, 350);
        return () => clearTimeout(timer);
    }, [query]);
    useEffect(() => {
        let active = true;
        setState((current) => ({...current, loading: true, error: null}));
        adminLearnerStateApi.courseSummary(courseId, {q: search, offset, limit: PAGE_SIZE})
            .then((data) => { if (active) setState({data, loading: false, error: null, fetchedAt: new Date()}); })
            .catch((error) => { if (active) setState((current) => ({...current, loading: false, error})); });
        return () => { active = false; };
    }, [courseId, search, offset, retry]);

    const {data, loading, error, fetchedAt} = state;
    const rows = Array.isArray(data?.items) ? data.items : [];
    const total = Number(data?.total) || 0;
    return <main className="als-page als-dashboard" dir="rtl">
        <nav className="als-breadcrumb" aria-label="مسیر صفحه"><Link to="/learner-state">وضعیت یادگیرندگان</Link><span>/</span><strong>پایش دورهٔ {faNumber(courseId)}</strong></nav>
        <header className="als-dashboard-header"><div><span className="als-eyebrow">مدیریت سامانه · فقط‌خواندنی</span><h1>پایش اعضای دورهٔ {faNumber(courseId)}</h1>
            <p>هر ردیف، وضعیت یک یادگیرنده در یک منبع دوره است.</p></div><Freshness at={fetchedAt} onRefresh={() => setRetry((value) => value + 1)}/></header>
        <div className="als-course-controls"><label htmlFor="als-course-query">جست‌وجو در اعضای دوره</label>
            <input id="als-course-query" type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="نام، ایمیل یا شمارهٔ دانش‌آموزی"/></div>
        {loading && <div className="als-skeleton-list" role="status" aria-label="در حال دریافت وضعیت افراد دوره"><i/><i/><i/></div>}
        {error && <div className="als-error" role="alert"><p>{error.status === 403 ? 'دسترسی به پایش این دوره ندارید.' : getErrorMessage(error)}</p>
            <button type="button" onClick={() => setRetry((value) => value + 1)}>تلاش دوباره</button></div>}
        {data && !loading && !error && <>
            <div className="als-course-scope"><span>{faNumber(data.scope?.resource_count)} منبع در دامنهٔ گزارش</span>
                {data.scope?.algorithm_version && <span>الگوریتم <bdi dir="ltr">{data.scope.algorithm_version}</bdi></span>}
                <span>{faNumber(total)} ردیف</span></div>
            {!rows.length ? <div className="als-empty-card">{data.scope?.resource_count === 0 ? 'این دوره منبعی برای پایش ندارد.' : 'ردیفی مطابق جست‌وجو پیدا نشد.'}</div>
                : <div className="als-data-table-wrap"><table className="als-data-table als-course-table"><thead><tr>
                    <th>یادگیرنده</th><th>منبع</th><th>تسلط</th><th>اطمینان</th><th>پوشش مفاهیم</th><th>آخرین شاهد</th><th>تغییر ۳۰ روزه</th><th>جزئیات</th>
                </tr></thead><tbody>{rows.map((row, index) => <tr key={`${row.learner_id}:${row.resource_id}:${row.graph_id ?? 'no-graph'}:${index}`}>
                    <td><Link className="als-table-primary" to={learnerPath(row, courseId)}>{row.display_name || `یادگیرندهٔ ${row.learner_id}`}</Link><small>{row.student_number || 'شماره ثبت نشده'}</small></td>
                    <td><Link to={detailPath(row, courseId)}>{row.resource_title || 'منبع بدون عنوان'}</Link><small>نسخهٔ {faNumber(row.resource_version)}</small></td>
                    <td><MasteryVisual value={row.mastery} status={row.status} compact/></td>
                    <td>{faPercent(row.confidence)}</td>
                    <td>{row.graph_id == null ? <span className="als-coverage-note">بدون گراف مفهومی</span> : <CoverageVisual assessed={row.assessed_concepts} total={row.total_concepts} value={row.coverage}/>}</td>
                    <td className="als-nowrap">{faDate(row.last_evidence_at)}</td><td><Change row={row}/></td>
                    <td><Link className="als-table-action" to={detailPath(row, courseId)}>دیدن منبع</Link></td>
                </tr>)}</tbody></table></div>}
            {total > PAGE_SIZE && <nav className="als-pager" aria-label="صفحه‌بندی افراد دوره"><span>{faNumber(offset + 1)} تا {faNumber(Math.min(offset + PAGE_SIZE, total))} از {faNumber(total)}</span>
                <button type="button" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>قبلی</button>
                <button type="button" disabled={offset + PAGE_SIZE >= total} onClick={() => setOffset(offset + PAGE_SIZE)}>بعدی</button></nav>}
            <p className="als-data-note">تغییر ۳۰ روزه فقط برای نقطه‌های هم‌نسخهٔ منبع و الگوریتم محاسبه می‌شود. در غیر این صورت دلیل نبود آن نمایش داده می‌شود.</p>
        </>}
    </main>;
}
